/**
 * entityProfilePhoto — pure helpers for the fleet-card / settings PROFILE image
 * (FT-436, 2026-08-08). VESSELS-ONLY SLICE.
 *
 * A profile image is a vessel-level picture shown on the fleet card and in the
 * vessel's Settings screen. It REUSES the existing photo pipeline wholesale
 * (Dexie `photos`/`photoThumbs` + `sr.photos.v1` sync + photoPullMerge) — see
 * useEntityProfilePhoto.js. This file holds only the pure, side-effect-free bits
 * so they can be unit-tested in isolation:
 *
 *   - PROFILE_RECORD_TYPE — the `recordType` tag that separates a profile photo
 *     from record-evidence photos in the SAME tables (additive; never collides
 *     with the FT-340 record recordTypes: equipment/maintenance/defect/…).
 *   - profilePhotoId(entityId) — DETERMINISTIC photoId. One profile photo per
 *     vessel: re-uploading overwrites (same id → same outbox recordId → the
 *     cross-device merge replaces, no orphan accretion).
 *   - PLACEHOLDER graphics — a small built-in set (no network, no asset files):
 *     inline SVG rendered to a data-URL, so a picked placeholder flows through
 *     the identical compress→save→sync path as an uploaded photo (a placeholder
 *     an admin picks DISPLAYS for crew, exactly like an uploaded photo).
 *   - dataUrlToBlob — decode a data-URL (from imageCompression or a placeholder
 *     SVG) → Blob for the photo store.
 *
 * FACILITY PATHS REMOVED: this vessels-only slice offers a boat placeholder
 * only — there is no marina/office/jetty/facility placeholder and no facility
 * entity kind anywhere.
 */

// recordType tag for profile photos. MUST be distinct from every record-evidence
// recordType (equipment/maintenance/defect/crew_profile/…) so a profile image is
// never confused with — and never cascade-deleted alongside — record evidence.
export const PROFILE_RECORD_TYPE = 'entity_profile';

/**
 * Deterministic photoId for a vessel's single profile photo.
 * `profile:<entityId>` — stable across re-uploads so the outbox recordId is
 * stable and the cross-device merge REPLACES rather than accreting orphans.
 * @param {string} entityId vessel.id
 * @returns {string}
 */
export const profilePhotoId = (entityId) => `profile:${entityId}`;

// ── Built-in placeholder graphics ────────────────────────────────────────────
// Small, neutral, brand-safe SVG marks. Kept minimal (single-colour glyph on a
// soft field) so they read clearly at card size (~w-full h-32) and as a thumb.
// NOTE: colours here are placeholder ARTWORK, not app chrome — the never-change-
// colours rule governs app UI, not user-selectable illustrative placeholders.

const svg = (glyph, bg = '#e2e8f0', fg = '#64748b') =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="384" viewBox="0 0 512 384">` +
    `<rect width="512" height="384" fill="${bg}"/>` +
    `<g fill="${fg}" transform="translate(156 92)">${glyph}</g>` +
    `</svg>`
  )}`;

// Glyphs are 200x200 viewBox paths (roughly centred by the translate above).
const GLYPHS = {
  // A simple boat/hull mark for vessels.
  boat:
    '<path d="M20 120 h160 l-24 44 a12 12 0 0 1-11 7 H55 a12 12 0 0 1-11-7 Z"/>' +
    '<rect x="94" y="30" width="12" height="90"/>' +
    '<path d="M106 40 l60 70 h-60 Z"/>',
};

/**
 * Built-in placeholder catalog. VESSELS-ONLY: a single boat mark. Every
 * placeholder is a self-contained SVG data-URL (no network / assets).
 */
export const PLACEHOLDERS = [
  { key: 'boat', label: 'Boat', entity: 'vessel', dataUrl: svg(GLYPHS.boat) },
];

/**
 * Placeholders offered for a given entity kind (vessels only in this slice).
 * @param {'vessel'} entityKind
 * @returns {Array<{key,label,entity,dataUrl}>}
 */
export const placeholdersFor = (entityKind) =>
  PLACEHOLDERS.filter((p) => p.entity === entityKind);

/**
 * Look up a placeholder by key.
 * @param {string} key
 * @returns {{key,label,entity,dataUrl}|null}
 */
export const getPlaceholder = (key) =>
  PLACEHOLDERS.find((p) => p.key === key) || null;

/**
 * Decode a data-URL (image/* or svg) → Blob. Works in browsers + jsdom via
 * `fetch` (same technique photoPullMerge uses). Falls back to manual base64
 * decode when `fetch` cannot handle the URL (defensive; not normally hit).
 * @param {string} dataUrl
 * @returns {Promise<Blob>}
 */
export const dataUrlToBlob = async (dataUrl) => {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
    throw new Error('[entityProfilePhoto] dataUrlToBlob: expected a data-URL string');
  }
  // Manual decode FIRST — synchronous, reliable in every env (browser + jsdom).
  // `fetch(dataUrl)` can hang under jest/jsdom, so we do not rely on it.
  try {
    const comma = dataUrl.indexOf(',');
    const header = dataUrl.slice(5, comma); // e.g. "image/jpeg;base64" | "image/svg+xml;utf8"
    const mime = header.split(';')[0] || 'application/octet-stream';
    const isBase64 = /;base64/i.test(header);
    const body = dataUrl.slice(comma + 1);
    if (isBase64) {
      const bin = atob(body);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    }
    // Non-base64 (e.g. utf8 SVG) — the body is percent-encoded text.
    return new Blob([decodeURIComponent(body)], { type: mime });
  } catch (_) {
    // Last resort: browser fetch (real runtime only).
    const res = await fetch(dataUrl);
    return res.blob();
  }
};
